import React, { useState, useCallback, useRef, useEffect, useLayoutEffect, memo } from 'react';
import { Handle, Position, NodeProps, useUpdateNodeInternals } from '@xyflow/react';
import styled from 'styled-components';
import { Tooltip, theme } from 'antd';
import { DeleteButton } from '../DeleteButton';
import { useStore, NodeData, Node, Edge } from '../../store/store';
import useTabManagerStore from '../../store/tabManagerStore';
import NodeIcon from './NodeIcon';
import { useNodeFields, formatFieldsForTooltip } from '../../utils/nodeUtils';
import { deleteNodeWithLogging } from '../../utils/deleteUtils';
import { createContextLogger } from '../../utils/logger';

const logger = createContextLogger('withNodeHandles');

// Type definitions
interface StepIconProps {
  data?: NodeData;
  type: string;
}

interface NodeWrapperProps {
  selected?: boolean;
}

interface WithNodeHandlesProps {
  data?: NodeData;
  id: string;
  type: string;
  selected?: boolean;
}

// Use object instead of empty object type
type ComponentWithNodeHandles<P = object> = React.ComponentType<P & WithNodeHandlesProps>;

// Main container for the node
const NodeWrapper = styled.div<NodeWrapperProps & { $bgColor?: string; $bgColorSelected?: string; $borderColor?: string }>`
  background-color: ${props => props.selected ? (props.$bgColorSelected || '#e6f7ff') : (props.$bgColor || '#fff')};
  border: 1px solid ${props => props.$borderColor || '#ddd'}; // Default border
  border-radius: 8px;
  box-shadow: ${props => props.selected ? '0 0 0 2px rgba(24, 144, 255, 0.2)' : '0 1px 3px rgba(0, 0, 0, 0.1)'};
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  overflow: visible; // Allow delete button to show outside node boundaries
  position: relative;
  transition:
    border-color 0.2s ease-in-out,
    border-width 0.2s ease-in-out,
    box-shadow 0.2s ease-in-out,
    background-color 0.2s ease-in-out;

  /* Apply thicker border when selected */
  ${({ selected }) =>
    selected &&
    `
    border-width: 2px;
    border-color: #777; /* Darker border color for selection */
  `}
`;

const IconWrapper = styled.div<{ $color?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: ${props => props.$color || '#555'};
`;

const LabelWrapper = styled.div<{ $color?: string }>`
  font-size: 12px;
  font-weight: 500;
  color: ${props => props.$color || '#333'};
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
  line-height: 1.2;
`;

// --- *** UPDATED: Icon Selection Logic *** ---
const StepIcon: React.FC<StepIconProps> = ({ data, type }) => {
  const iconStyle: React.CSSProperties = {
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  // Use the new NodeIcon component that handles both static and dynamic icons
  return (
    <NodeIcon
      type={data?.type || data?.iconType} // Use step type for icon lookup
      nodeType={type} // Pass ReactFlow node type
      style={iconStyle}
    />
  );
};
// --- *** END UPDATE *** ---

// Easy Connect Pattern: No need for dynamic handle positioning
// The entire node acts as a connection handle via invisible handles

export const withNodeHandles = <P extends WithNodeHandlesProps>(
  WrappedComponent: React.ComponentType<P>
): ComponentWithNodeHandles<Omit<P, keyof WithNodeHandlesProps>> => {
  const NodeWithHandles: React.FC<WithNodeHandlesProps> = ({ 
    data, 
    id, 
    type, 
    selected, 
    ...props 
  }) => {
    const { token } = theme.useToken();
    const [showDelete, setShowDelete] = useState<boolean>(false);
    const deleteNode = useStore((state) => state.deleteNode);
    const markStructuralChange = useStore((state) => state.markStructuralChange);
    const updateTabModified = useTabManagerStore((state) => state.updateTabModified);
    const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const updateNodeInternals = useUpdateNodeInternals();
    
    // Get selected node info and pipelineId from store
    const { selectedNodeId, pipelineId } = useStore((state) => {
      const activePipeline = state.pipelines.find((p) => p.id === state.activePipelineId);
      return {
        selectedNodeId: activePipeline?.selectedNodeId,
        pipelineId: state.activePipelineId,
      };
    });

    const isSelected = id === selectedNodeId;
    
    // Get the node fields using the existing hook (for tooltips)
    const { incomingFields, outgoingFields } = useNodeFields(id);

    // Create the tooltip content
    const incomingTooltip = formatFieldsForTooltip(incomingFields);
    const outgoingTooltip = formatFieldsForTooltip(outgoingFields);

    const clearHideTimeout = useCallback((): void => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
    }, []);

    const scheduleHide = useCallback((): void => {
      clearHideTimeout();
      hideTimeoutRef.current = setTimeout(() => {
        setShowDelete(false);
      }, 1000);
    }, [clearHideTimeout]);

    const handleMouseEnter = useCallback((): void => {
      clearHideTimeout();
      setShowDelete(true);
    }, [clearHideTimeout]);

    const handleMouseLeave = useCallback((): void => {
      scheduleHide();
    }, [scheduleHide]);

    const handleDeleteButtonMouseEnter = useCallback((): void => {
      clearHideTimeout();
    }, [clearHideTimeout]);

    const handleDeleteButtonMouseLeave = useCallback((): void => {
      scheduleHide();
    }, [scheduleHide]);

    const handleDelete = useCallback((event: React.MouseEvent<HTMLElement>): void => {
      logger.debug('Node delete handler called for:', id);
      event.preventDefault();
      event.stopPropagation();
      clearHideTimeout();
      setShowDelete(false);
      
      // Use the shared delete utility for consistency
      if (pipelineId) {
        deleteNodeWithLogging({
          nodeId: id,
          deleteNode,
          updateTabModified,
          markStructuralChange,
          pipelineId,
        });
      }
    }, [deleteNode, id, clearHideTimeout, updateTabModified, markStructuralChange, pipelineId]);

    // Force React Flow to remeasure when data changes (for dynamic width)
    useLayoutEffect(() => {
      updateNodeInternals(id);
    }, [data?.name, data?.label, data?.title, id, updateNodeInternals]);

    // Clean up timeout on unmount
    useEffect(() => {
      return () => {
        clearHideTimeout();
      };
    }, [clearHideTimeout]);

    // Generate random status color for demo purposes
    const getRandomStatusColor = React.useMemo(() => {
      const colors = [
        'transparent',        // Idle
        token.colorSuccess,   // Success (green)
        token.colorError,     // Error (red)
        token.colorWarning,   // Warning (yellow)
        token.colorPrimary,   // Running (blue)
        token.colorInfo,      // Info (light blue)
      ];
      return colors[Math.floor(Math.random() * colors.length)];
    }, [id, token]); // Re-generate per node

    return (
      <>
        {/* Drag handle bar - hollow bar that can be filled with status colors */}
        <div
          style={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '35px',
            height: '8px',
            borderRadius: '0 0 4px 4px', // Flipped: rounded corners at bottom
            border: `1px solid ${token.colorBorder}`,
            backgroundColor: getRandomStatusColor, // Random color for demo
            cursor: 'grab',
            zIndex: 10,
            transition: 'opacity 0.2s',
            opacity: isSelected ? 1 : 0.5,
            // Future: add data-status attribute to change backgroundColor
            // backgroundColor: statusColor (e.g., token.colorSuccess, token.colorError)
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = '1';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = isSelected ? '0.9' : '0.5';
          }}
        />
        
        <NodeWrapper
          selected={isSelected}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          data-testid="react-flow-node"
          $bgColor={token.colorBgContainer}
          $bgColorSelected={token.colorPrimaryBg}
          $borderColor={token.colorBorder}
          className="nodrag" // Prevent the node itself from being draggable
        >
          {/* Easy Connect Pattern: Both source and target handles covering the entire node */}
          {/* This allows connections to start and end anywhere on the node */}
          
          {/* Source handle - for creating outgoing connections */}
          <Handle
            type="source"
            position={Position.Bottom} // Position doesn't matter with full coverage, but Bottom is conventional
            id="source"
            style={{
              width: '100%',
              height: '100%',
              background: 'transparent',
              border: 'none',
              borderRadius: '8px',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              transform: 'none',
              opacity: 0,
              cursor: 'crosshair',
            }}
            isConnectable={true}
          />

          {/* Target handle - for receiving incoming connections */}
          <Handle
            type="target"
            position={Position.Top} // Position doesn't matter with full coverage, but Top is conventional
            id="target"
            style={{
              width: '100%',
              height: '100%',
              background: 'transparent',
              border: 'none',
              borderRadius: '8px',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              transform: 'none',
              opacity: 0,
            }}
            isConnectable={true}
          />

          <IconWrapper $color={token.colorText}>
            <StepIcon data={data} type={type} />
          </IconWrapper>

          {showDelete && (
            <div
              style={{
                position: 'absolute',
                top: -12,
                right: -12,
                zIndex: 1001,
                pointerEvents: 'auto',
              }}
              onMouseEnter={handleDeleteButtonMouseEnter}
              onMouseLeave={handleDeleteButtonMouseLeave}
            >
              <DeleteButton 
                visible={true} 
                onDelete={handleDelete} 
                isEdge={false}
              />
            </div>
          )}
        </NodeWrapper>

        {/* Label positioned absolutely below, outside React Flow measurement */}
        <LabelWrapper 
          style={{ 
            position: 'absolute',
            top: '52px', // 48px node height + 4px gap
            left: '50%',
            transform: 'translateX(-50%)',
            whiteSpace: 'nowrap',
          }} 
          $color={token.colorText}
        >
          <WrappedComponent 
            {...(props as P)}
            data={data} 
            id={id} 
            type={type} 
            selected={isSelected} 
          />
        </LabelWrapper>
      </>
    );
  };
  
  // Set display name for better debugging
  NodeWithHandles.displayName = `withNodeHandles(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;
  
  return memo(NodeWithHandles) as ComponentWithNodeHandles<Omit<P, keyof WithNodeHandlesProps>>;
};
